import { useMutation } from '@apollo/client';
import { AncestorIdData, AncestorNameData } from 'modules/domains/foldering';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { FolderNameLink } from 'ui/indexPages/folders/Folders.styles';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { FOLDER_ID_SEARCH_PARAM, PAGE_SEARCH_PARAM, ROOT_FOLDER_NAME } from '../constants';
import { useCallback } from 'react';
import { CreateAssetFolderQuery } from 'api/queries/creativeLibrary';

type CreateFolderResponseData = {
  createAssetFolder: number;
};

type Variables = {
  name: string;
  locationId?: number | null;
};

const displayText = {
  genericError: 'Could not create folder',
};

export const useCreateAssetFolder = () => {
  const dispatch = useDispatch();

  const [createFolder] = useMutation<CreateFolderResponseData, Variables>(CreateAssetFolderQuery);

  return useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      try {
        const { data: creationMutationData, errors: creationMutationErrors } = await createFolder({
          variables: {
            name: newFolderName,
            locationId: newFolderDestination.id,
          },
        });

        const destinationFolderName =
          newFolderDestination.name === '__root__' ? ROOT_FOLDER_NAME : newFolderDestination.name;

        if (!creationMutationData?.createAssetFolder || creationMutationErrors) {
          dispatch(
            addNegativeToast(
              <ResponseWrapper key="toast-error">
                <NamedFolder name={newFolderName} /> could not be created
                {destinationFolderName ? (
                  <span>
                    {' '}
                    in the <NamedFolder name={destinationFolderName} /> folder.
                  </span>
                ) : (
                  '.'
                )}
              </ResponseWrapper>,
            ),
          );
          return;
        }

        const newFolderId = creationMutationData.createAssetFolder;
        const { location } = window;
        const urlParams = new URLSearchParams(location.search);
        urlParams.set(FOLDER_ID_SEARCH_PARAM, newFolderId.toString());
        urlParams.delete(PAGE_SEARCH_PARAM);

        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <FolderNameLink to={`${location.pathname}?${urlParams.toString()}`}>
                <NamedFolder name={newFolderName} />
              </FolderNameLink>{' '}
              was successfully created
              {destinationFolderName ? (
                <span>
                  {' '}
                  in the <NamedFolder name={destinationFolderName} /> folder.
                </span>
              ) : (
                '.'
              )}
            </ResponseWrapper>,
          ),
        );
      } catch (e) {
        dispatch(addNegativeToastWithErrorId(displayText.genericError, 'useCreateAssetFolder', e));
      }
    },
    [createFolder, dispatch],
  );
};
